import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2, Utensils } from "lucide-react";
import { toast } from "sonner";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_META = {
  pending: {
    tone: "bg-amber-50 border-amber-200 text-amber-900",
    icon: <Clock className="h-5 w-5" />,
    title: "Awaiting confirmation",
    message: "Your request is with the restaurant. We usually confirm within a few hours.",
  },
  confirmed: {
    tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
    icon: <CheckCircle2 className="h-5 w-5" />,
    title: "Reservation confirmed",
    message: "Your table is booked. See you at Caldera View.",
  },
  seated: {
    tone: "bg-blue-50 border-blue-200 text-blue-900",
    icon: <Utensils className="h-5 w-5" />,
    title: "Guests seated",
    message: "You're at the table — enjoy your evening.",
  },
  cancelled: {
    tone: "bg-stone-100 border-stone-300 text-stone-700",
    icon: <XCircle className="h-5 w-5" />,
    title: "Reservation cancelled",
    message: "This booking has been cancelled. Please contact us if this wasn't intended.",
  },
};

export default function ConfirmationPage() {
  const { code } = useParams();
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let previousStatus = null;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/reservations/lookup/${code}`);
        if (cancelled) return;
        if (previousStatus && data.status !== previousStatus) {
          if (data.status === "confirmed") toast.success("Your reservation was just confirmed!");
          else if (data.status === "cancelled") toast.error("Your reservation was cancelled.");
          else if (data.status === "seated") toast.success("You've been marked as seated.");
        }
        previousStatus = data.status;
        setRes(data);
      } catch {
        if (!cancelled) setError("We couldn't find that reservation.");
      }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, 15000); // poll every 15s for live status updates
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [code]);

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="confirmation-page">
      <SiteHeader />
      <div className="pt-32 pb-24 px-6 md:px-10">
        <div className="max-w-2xl mx-auto bg-white border border-[#E5E0D8] rounded-lg p-10 md:p-14">
          {!res && !error && (
            <div className="text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#D35400]" />
            </div>
          )}
          {error && (
            <div className="text-center">
              <h1 className="font-display text-3xl">Not found</h1>
              <p className="mt-3 text-[#8C7A6B]">{error}</p>
              <Link to="/book" className="inline-block mt-6">
                <Button className="btn-terracotta rounded-full h-11 px-6">Make a reservation</Button>
              </Link>
            </div>
          )}
          {res && (
            <>
              <div className="text-center">
                <div className="overline">Reservation received</div>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl" data-testid="confirmation-title">
                  We look forward to welcoming you.
                </h1>
              </div>

              {/* LIVE STATUS BANNER */}
              <div
                className={`mt-8 border rounded-lg p-5 flex items-start gap-4 ${STATUS_META[res.status]?.tone || ""}`}
                data-testid={`status-banner-${res.status}`}
              >
                <div className="mt-0.5">{STATUS_META[res.status]?.icon}</div>
                <div className="flex-1">
                  <div className="font-medium text-sm" data-testid="confirmation-status">
                    {STATUS_META[res.status]?.title || res.status}
                  </div>
                  <div className="text-xs mt-1 opacity-80">
                    {STATUS_META[res.status]?.message}
                  </div>
                </div>
                {res.status === "pending" && (
                  <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                    Live
                  </div>
                )}
              </div>

              <div className="mt-8 border-t border-b border-[#E5E0D8] py-6 grid grid-cols-2 gap-4 text-left text-sm">
                <Row label="Reference" value={<span className="font-display text-xl text-[#D35400]" data-testid="confirmation-code">{res.reference_code}</span>} />
                <Row label="Guest" value={res.name} />
                <Row label="Party" value={`${res.party_size} guest${res.party_size > 1 ? "s" : ""}`} />
                <Row label="Date" value={res.date} />
                <Row label="Time" value={res.time_slot} />
                <Row label="Phone" value={res.phone} />
                {res.balcony_requested && (
                  <div className="col-span-2" data-testid="confirmation-balcony">
                    <div className="overline">Balcony table</div>
                    <div className="mt-1 text-[#D35400] text-sm">Requested — subject to availability and a minimum‑spend fee.</div>
                  </div>
                )}
                {res.balcony_reallocated && (
                  <div className="col-span-2" data-testid="confirmation-balcony-reallocated">
                    <div className="overline">Balcony update</div>
                    <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 p-4 text-sm leading-relaxed">
                      Unfortunately no balcony table is available for your date and time, but we've confirmed your reservation for another table. We look forward to welcoming you.
                    </div>
                  </div>
                )}
              </div>

              <p className="mt-6 text-sm text-[#8C7A6B] text-center">
                Keep this page open — the status will update automatically the moment the restaurant confirms.
                You can also return anytime with your reference code at <Link to="/lookup" className="underline hover:text-[#D35400]">/lookup</Link>.
              </p>

              {/* IMPORTANT INFO */}
              <div className="mt-8 border border-[#E5E0D8] rounded-lg bg-[#F9F6F0] p-6 text-left" data-testid="confirmation-info">
                <div className="overline">Before you arrive</div>
                <ul className="mt-4 space-y-3 text-sm text-[#1A2B3C] leading-relaxed">
                  <li className="flex gap-3">
                    <span className="text-[#D35400] font-bold">·</span>
                    <span>Your specific table will be assigned and shown to you when you arrive at the restaurant.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#D35400] font-bold">·</span>
                    <span>Reservations are valid for <strong>dinner tables only</strong>.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#D35400] font-bold">·</span>
                    <span>Our two <strong>balcony tables</strong> are available upon request and are subject to a <strong>minimum-spend fee</strong>.</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 flex justify-center gap-3">
                <Link to="/">
                  <Button variant="outline" className="rounded-full h-11 px-6 border-[#E5E0D8]" data-testid="confirmation-home-btn">Return home</Button>
                </Link>
                <Link to="/book">
                  <Button className="btn-terracotta rounded-full h-11 px-6" data-testid="confirmation-new-btn">New reservation</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-1 text-[#1A2B3C]">{value}</div>
    </div>
  );
}
